import { trackEvent } from '@repo/common/analytics';
import { useCallback, useMemo, useState } from 'react';
import {
	type IDatabuilderJob,
	integrationsQueryKeyFactory,
	queryClient,
	useAuthUser,
	useCreateDatabuilderJob,
	useIntegration,
	useIntegrationSpec,
} from '../../../api';
import {
	forceFetchJobs,
	usePollDatabuilderJobList,
} from '../../../api/hooks/databuilderJob/usePollDatabuilderJob';
import { DatabuilderJobType, SyncType } from '../../../interfaces';
import {
	getIdentityTokenIfOidc,
	shouldCancelSyncForOidc,
} from '../identityTokenHelpers';

interface UseIntegrationSyncProps {
	id: string;
	type?: SyncType;
	page?: number;
}

const useIntegrationExtraction = ({
	id,
	type = SyncType.PULL,
	page = 1,
}: UseIntegrationSyncProps) => {
	const { user, workspace } = useAuthUser();

	// Add this to get integration spec
	const { data: integration } = useIntegration({ id });
	const integrationSpec = useIntegrationSpec(id);

	const {
		databuilderJobs: extractionJobs,
		polling,
		startPolling,
		stopPolling,
	} = usePollDatabuilderJobList({
		integrationId: id,
		type,
		page,
	});

	const { mutate: createJob } = useCreateDatabuilderJob({
		options: {
			onSuccess: () => {
				startPolling();
				forceFetchJobs();
				queryClient.invalidateQueries({
					queryKey: integrationsQueryKeyFactory.allLists(),
				});
			},
		},
	});

	const [extractionDetailId, setExtractionDetailId] = useState<string | null>(
		null
	);

	const handleRunExtraction = useCallback(async () => {
		if (polling) {
			return;
		}

		const identityToken = await getIdentityTokenIfOidc({
			integration,
		});

		const { shouldCancel } = shouldCancelSyncForOidc(identityToken, {
			integration,
		});

		if (shouldCancel) {
			return;
		}

		try {
			await createJob({
				data: {
					integration_id: id,
					type: DatabuilderJobType.METADATA,
					...(identityToken && { identity_token: identityToken }),
				},
			});
			trackEvent('integrations/run_extraction/click', {}, user, workspace!);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
		}
	}, [createJob, id, polling, user, workspace, integrationSpec, integration]);

	const handleOpenActiveExtraction = useCallback((job: IDatabuilderJob) => {
		setExtractionDetailId(job.id);
	}, []);

	const handleCloseExtractionDetail = useCallback(() => {
		setExtractionDetailId(null);
	}, []);

	const extractionDetailJob = useMemo(
		() => extractionJobs?.find((job) => job.id === extractionDetailId),
		[extractionJobs, extractionDetailId]
	);

	return {
		extractionJobs,
		polling,
		stopPolling,
		handleRunExtraction,
		handleOpenActiveExtraction,
		handleCloseExtractionDetail,
		extractionDetailJob,
	};
};

export default useIntegrationExtraction;
